import { AxiosInstance, AxiosResponse } from 'axios';
import { config } from 'config';
import * as Sentry from '@sentry/react';
import { SERVICE_DOWN } from 'api/errors';
import { AxiosClient, AxiosUtils } from '../AxiosClient/AxiosClient';
import {
  Deposit,
  Strength,
  RegistrationUnicityResponse,
  BadgesResponse,
  VehicleType,
  ParkingResponse,
  Strengths,
} from 'types';
import { formatDateForBadges } from 'utils/date';
import { Classified, Vehicle } from '@groupelacentrale/classified-core';
import { verticals } from 'utils';

class Classifieds {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_CLASSIFIEDS_API,
    });
  }

  async isRegistrationAvailable(registration?: string): Promise<RegistrationUnicityResponse> {
    if (!registration) {
      return Promise.resolve({ available: true });
    }
    return this.client
      .get<RegistrationUnicityResponse>(`v2/registration/${registration}/availability`, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => result.data)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classifieds',
            endpoint: 'unicity v2',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response) {
          const response: AxiosResponse = r.response;
          return Promise.reject(response.data);
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  async isVinAvailable(vin?: string): Promise<RegistrationUnicityResponse> {
    if (!vin) {
      return Promise.resolve({ available: true });
    }
    return this.client
      .get<RegistrationUnicityResponse>(`v1/vin/${vin}/availability`, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => result.data)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classifieds',
            endpoint: 'unicity vin v1',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response) {
          const response: AxiosResponse = r.response;
          return Promise.reject(response.data);
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  async isParkingFull(customerReference: string): Promise<boolean> {
    return this.client
      .get<ParkingResponse>(`/v1/customers/${customerReference}/parking`, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(r => r.data.occupiedSize >= r.data.maxSize)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classifieds',
            endpoint: 'parking',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response) {
          const response: AxiosResponse<any> = r.response;
          return Promise.reject({ message: response.data.error });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async getStrengths(deposit: Deposit | Classified): Promise<Strength[]> {
    const vehicle = deposit.vehicle as Vehicle;
    const type = (deposit as Deposit).depositType || verticals[(deposit as Classified).vehicle.category];

    if (!type) {
      return Promise.reject({ message: 'Impossible to get strengths without vertical' });
    }

    const data =
      (deposit as Classified).status === 'ONLINE'
        ? { reference: deposit.reference }
        : {
            equipmentsOrOptions: vehicle.equipmentsOrOptions ? vehicle.equipmentsOrOptions : [],
            firstHand: vehicle.firstHand,
            vertical: Classifieds.mapVehicleType(type),
            seats: vehicle.seats ?? 0,
            brand: vehicle.make,
            model: vehicle.model,
            version: vehicle.version,
            licencePlate: vehicle.registerNumber,
            category: vehicle.category,
            mec: formatDateForBadges(new Date(vehicle.firstCirculationDate!)),
            price: deposit.price,
            energy: vehicle.energy,
            co2: vehicle.computedCo2?.combined ?? 0,
            modelCom: vehicle.commercialName,
            mileage: vehicle.mileage,
            zipcode: deposit.zipCode,
            fiscalHorsePower: vehicle.ratedHorsePower,
            powerDin: vehicle.powerDIN,
            gearbox: vehicle.gearbox,
            nbOfDoors: vehicle.doors,
          };

    return this.client
      .post<Strengths>('v2/simulation/final', data, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(r => r.data.strengths)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classifieds',
            endpoint: 'simulation/final',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response) {
          const response: AxiosResponse<any> = r.response;
          return Promise.reject({ message: response.data.error });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async getBadges(
    vehicle: Vehicle,
    depositType: VehicleType,
    zipCode: string,
  ): Promise<BadgesResponse | Record<string, never>> {
    return this.client
      .post<BadgesResponse | Record<string, never>>(
        'v3/simulation/badges',
        {
          category: vehicle.category,
          seats: vehicle.seats,
          vertical: Classifieds.mapVehicleType(depositType!),
          brand: vehicle.make,
          model: vehicle.model,
          modelCom: vehicle.commercialName,
          version: vehicle.version,
          mileage: vehicle.mileage,
          mec: formatDateForBadges(new Date(vehicle.firstCirculationDate!)),
          firstHand: vehicle.firstHand,
          licencePlate: vehicle.registerNumber,
          zipcode: zipCode,
          fiscalHorsePower: vehicle.ratedHorsePower,
          powerDin: vehicle.powerDIN,
          energy: vehicle.energy,
          gearbox: vehicle.gearbox,
          nbOfDoors: vehicle.doors,
        },
        { headers: AxiosClient.getAuthorizationHeader() },
      )
      .then(r => r.data)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classifieds',
            endpoint: 'simulation/badges',
            ...AxiosUtils.enrichTags(r),
          },
        });
        return {};
      });
  }

  private static mapVehicleType(vehicleType: VehicleType): string {
    if (vehicleType === 'AUTO' || vehicleType === 'MOTO') {
      return vehicleType;
    } else if (vehicleType === 'CAMPING-CAR' || vehicleType === 'CARAVANE') {
      return 'LOISIR';
    } else if (vehicleType === 'UTILITAIRE') {
      return 'UTILS';
    }
    return vehicleType;
  }
}

export const ClassifiedsService = new Classifieds();
